<template>
  <div>
    <div class="h-[100vh] w-[240px] bg-gray-900 text-white fixed flex flex-col">

      <!-- Brand -->
      <div class="h-[70px] flex items-center justify-center border-b border-gray-700 shrink-0">
        <span class="text-[17px] font-bold tracking-wide text-white">Asset Tracker</span>
      </div>

      <!-- User info -->
      <div class="px-5 py-5 border-b border-gray-700 shrink-0">
        <div class="text-[15px] font-semibold text-white truncate">{{ name }}</div>
        <div class="text-[12px] text-gray-400 truncate mt-1">{{ role }}</div>
      </div>

      <!-- Nav -->
      <nav :key="permissionVersion" class="flex flex-col flex-1 pt-3 overflow-y-auto">

        <router-link v-if="canShow(MENU_PERMISSION_MATRIX.assets)" to="/home" class="nav-link" active-class="nav-link-active">
          <svg class="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/></svg>
          Assets
        </router-link>

        <template v-if="canShow(MENU_PERMISSION_MATRIX.users)">
          <router-link to="/users" class="nav-link" active-class="nav-link-active">
            <svg class="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
            Users
          </router-link>
        </template>

        <template v-if="canShow(MENU_PERMISSION_MATRIX.registerUser)">
          <router-link to="/register-user" class="nav-link" active-class="nav-link-active">
            <svg class="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
            Add User
          </router-link>
        </template>

        <template v-if="canShow(MENU_PERMISSION_MATRIX.roleManagement)">
          <router-link to="/roles" class="nav-link" active-class="nav-link-active">
            <svg class="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            Roles & Permissions
          </router-link>
        </template>

        <router-link v-if="canShow(MENU_PERMISSION_MATRIX.profile)" to="/profile" class="nav-link" active-class="nav-link-active">
          <svg class="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          My Profile
        </router-link>

        <router-link v-if="canShow(MENU_PERMISSION_MATRIX.changePassword)" to="/change-password" class="nav-link" active-class="nav-link-active">
          <svg class="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          Change Password
        </router-link>

      </nav>

      <!-- Logout -->
      <div class="shrink-0 border-t border-gray-700">
        <button
          @click="logout"
          :disabled="loggingOut"
          class="w-full flex items-center gap-3 px-5 py-4 text-[14px] font-medium text-gray-400 hover:bg-gray-800 hover:text-red-400 transition-colors disabled:opacity-50"
        >
          <svg class="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          {{ loggingOut ? 'Logging out...' : 'Log Out' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import api from "../../../boot/axios.js";
import { setCurrentUser } from "@/store/index.js";
import { canAll, canAny, isAuthenticated, isSuperadmin } from "@/auth/permissions";
import { MENU_PERMISSION_MATRIX } from "@/config/permissionMatrix";

const router = useRouter();

const name         = computed(() => setCurrentUser.value || localStorage.getItem("name") || "User");
const role         = ref(localStorage.getItem("role") || "");
const loggingOut   = ref(false);
const permissionVersion = ref(0);

const onAuthSessionUpdated = () => {
  role.value = localStorage.getItem("role") || "";
  permissionVersion.value += 1;
};

onMounted(() => {
  window.addEventListener("auth-session-updated", onAuthSessionUpdated);
});

onBeforeUnmount(() => {
  window.removeEventListener("auth-session-updated", onAuthSessionUpdated);
});

const canShow = ({ requiredAnyPermissions = [], requiredAllPermissions = [] } = {}) => {
  if (!isAuthenticated()) {
    return false;
  }

  if (isSuperadmin()) {
    return true;
  }

  if (requiredAnyPermissions.length === 0 && requiredAllPermissions.length === 0) {
    return true;
  }

  const anyPass = requiredAnyPermissions.length > 0 ? canAny(requiredAnyPermissions) : true;
  const allPass = requiredAllPermissions.length > 0 ? canAll(requiredAllPermissions) : true;
  return anyPass && allPass;
};

const logout = async () => {
  loggingOut.value = true;
  try {
    const r_token = localStorage.getItem("r_token");
    if (r_token) {
      await api.post("/auth/logout", { r_token });
    }
  } catch {
    // still clear even if API fails
  } finally {
    localStorage.clear();
    router.push("/");
  }
};
</script>

<style scoped>
.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #9ca3af;
  transition: background-color 0.15s, color 0.15s;
  border-left: 3px solid transparent;
  white-space: nowrap;
  overflow: hidden;
}
.nav-link:hover {
  background-color: #1f2937;
  color: #ffffff;
}
.nav-link-active {
  background-color: #1f2937;
  color: #ffffff;
  font-weight: 600;
  border-left-color: #ffffff;
}
</style>